import React from 'react';
import type { ResolvedPhoneShell, ThreadView } from 'magical-trails';

import Avatar from '../Avatar';
import { clockFace } from '../clock';
import { previewFor } from '../messageContent';

export interface Props {
  threads: ThreadView[];
  shell: ResolvedPhoneShell;
  title: string;
  onBack?: () => void;
  onOpen: (threadKey: string) => void;
}

function plainLine(html: string | undefined): string {
  return (html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function lastLine(thread: ThreadView): string {
  if (thread.typing) return `${thread.typing.name} is typing…`;
  const message = thread.messages[thread.messages.length - 1];
  if (!message) return '';
  if (message.delivery.presentation === 'attachment') {
    return message.delivery.attachmentLabel?.trim() || message.point.title;
  }
  const pages = message.point.pages ?? [];
  const page = pages[Math.min(message.cursor, pages.length - 1)];
  // A finished exchange ends on what the sender said back, not their ask.
  const answeredBack =
    message.state === 'done' && page && 'completeText' in page
      ? plainLine(page.completeText)
      : '';
  const line = answeredBack || previewFor(page);
  return thread.isGroup ? `${message.sender.name}: ${line}` : line;
}

const clock = (at: number) => clockFace(new Date(at));

const ThreadList: React.FC<Props> = ({ threads, title, onBack, onOpen }) => (
  <section className="Phone_screen Phone_threads" aria-label={title}>
    <header className="Phone_appBar">
      {onBack && (
        <button
          type="button"
          className="Phone_appBarButton"
          onClick={onBack}
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 5 8 12l7 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
      <div className="Phone_appBarTitle Phone_appBarTitle-large">
        <h1>{title}</h1>
      </div>
    </header>

    {threads.length === 0 ? (
      <div className="Phone_empty">
        <p className="Phone_emptyTitle">No messages yet</p>
        <p>They will turn up here as you explore.</p>
      </div>
    ) : (
      <ul className="Phone_threadList">
        {threads.map((thread) => (
          <li key={thread.key}>
            <button
              type="button"
              className={`Phone_threadRow ${
                thread.unreadCount > 0 ? 'Phone_threadRow-unread' : ''
              }`}
              onClick={() => onOpen(thread.key)}
            >
              <Avatar
                name={thread.name}
                src={thread.avatar}
                group={thread.isGroup}
              />
              <span className="Phone_threadText">
                <span className="Phone_threadName">{thread.name}</span>
                <span className="Phone_threadPreview">{lastLine(thread)}</span>
              </span>
              <span className="Phone_threadMeta">
                <time dateTime={new Date(thread.lastActivity).toISOString()}>
                  {clock(thread.lastActivity)}
                </time>
                {thread.unreadCount > 0 ? (
                  <span className="Phone_badge">
                    {thread.unreadCount}
                    <span className="sr-only"> unread</span>
                  </span>
                ) : thread.waitingOnPlayer ? (
                  <span className="Phone_replyDue">
                    Reply<span className="sr-only">: waiting for you</span>
                  </span>
                ) : null}
              </span>
            </button>
          </li>
        ))}
      </ul>
    )}
  </section>
);

export default ThreadList;
